/**
 * Anonymous Session Analytics — tracks engagement for teacher dashboards.
 *
 * No PII collected. All data stored locally in sessionStorage (cleared on tab close)
 * and optionally sent to a lightweight analytics endpoint.
 *
 * Metrics tracked:
 * - Session duration
 * - Query count (voice vs typed)
 * - Feature usage (navigation, choropleth, POI, boundaries, terrain, etc.)
 * - Countries/regions explored
 * - Educational questions asked
 * - Errors encountered
 */

import { getSessionStandards } from '../data/curriculum.js';

const API_URL = import.meta.env.VITE_API_URL || '';

class SessionAnalytics {
  constructor() {
    this.sessionId = crypto.randomUUID?.() || Math.random().toString(36).slice(2);
    this.startTime = Date.now();
    this.promptLog = []; // Every prompt with outcome
    this.metrics = {
      queries: 0,
      voiceQueries: 0,
      typedQueries: 0,
      featureUsage: {},
      countriesExplored: new Set(),
      educationalQuestions: 0,
      errors: 0,
      fastQueryHits: 0,
      aiQueryHits: 0,
      h3DensityViews: 0,
      terrainToggles: 0,
      overtureLayerToggles: 0,
    };
  }

  /**
   * Record a query event.
   * @param {string} type - Intent type (navigation, choropleth, filter, etc.)
   * @param {object} options - { inputMethod, country, isFastQuery }
   */
  trackQuery(type, options = {}) {
    this.metrics.queries++;

    if (options.inputMethod === 'voice') {
      this.metrics.voiceQueries++;
    } else {
      this.metrics.typedQueries++;
    }

    if (options.isFastQuery) {
      this.metrics.fastQueryHits++;
    } else {
      this.metrics.aiQueryHits++;
    }

    // Track feature usage
    this.metrics.featureUsage[type] = (this.metrics.featureUsage[type] || 0) + 1;

    // Track countries explored
    if (options.country) {
      this.metrics.countriesExplored.add(options.country);
    }

    // Track specific features
    if (type === 'educational') this.metrics.educationalQuestions++;
    if (type === 'terrain') this.metrics.terrainToggles++;
    if (type === 'overture_layer') this.metrics.overtureLayerToggles++;
    if (options.visualization === 'h3-density') this.metrics.h3DensityViews++;

    // Record prompt in the log
    this.promptLog.push({
      ts: Date.now(),
      prompt: options.prompt || '',
      intentType: type,
      inputMethod: options.inputMethod || 'typed',
      resolution: options.isFastQuery ? 'fast-query' : options.isFallback ? 'fallback' : 'ai',
      country: options.country || null,
      visualization: options.visualization || null,
      error: false,
    });
  }

  trackError(prompt, errorMessage) {
    this.metrics.errors++;
    this.promptLog.push({
      ts: Date.now(),
      prompt: prompt || '',
      intentType: 'error',
      inputMethod: null,
      resolution: 'error',
      country: null,
      visualization: null,
      error: true,
      errorMessage: errorMessage || 'Unknown error',
    });
  }

  /**
   * Get a summary of the current session for display or export.
   */
  getSummary() {
    const duration = Math.round((Date.now() - this.startTime) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;

    return {
      sessionId: this.sessionId,
      duration: `${minutes}m ${seconds}s`,
      durationSeconds: duration,
      totalQueries: this.metrics.queries,
      voiceQueries: this.metrics.voiceQueries,
      typedQueries: this.metrics.typedQueries,
      countriesExplored: [...this.metrics.countriesExplored],
      countriesCount: this.metrics.countriesExplored.size,
      educationalQuestions: this.metrics.educationalQuestions,
      featureUsage: { ...this.metrics.featureUsage },
      fastQueryHits: this.metrics.fastQueryHits,
      aiQueryHits: this.metrics.aiQueryHits,
      errors: this.metrics.errors,
      standardsCovered: getSessionStandards(this.metrics.featureUsage),
    };
  }

  /**
   * Save session summary to sessionStorage (persists until tab close).
   */
  save() {
    try {
      const summary = this.getSummary();
      sessionStorage.setItem('ev_session', JSON.stringify(summary));

      // Also maintain a history of sessions in localStorage (for teacher dashboard)
      const history = JSON.parse(localStorage.getItem('ev_session_history') || '[]');
      history.push({
        ...summary,
        timestamp: new Date().toISOString(),
      });
      // Keep last 100 sessions
      if (history.length > 100) history.splice(0, history.length - 100);
      localStorage.setItem('ev_session_history', JSON.stringify(history));
    } catch {
      // Storage quota exceeded or not available — silently ignore
    }
  }

  /**
   * Get session history from localStorage (for teacher dashboard).
   */
  static getHistory() {
    try {
      return JSON.parse(localStorage.getItem('ev_session_history') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Clear session history.
   */
  static clearHistory() {
    localStorage.removeItem('ev_session_history');
  }

  /**
   * Send session prompt report to the Worker for product analytics.
   * Uses navigator.sendBeacon for reliable delivery on page unload.
   */
  sendReport() {
    if (this.promptLog.length === 0) return;

    const report = {
      sessionId: this.sessionId,
      startTime: this.startTime,
      endTime: Date.now(),
      durationSeconds: Math.round((Date.now() - this.startTime) / 1000),
      totalQueries: this.metrics.queries,
      errors: this.metrics.errors,
      prompts: this.promptLog,
    };

    const url = `${API_URL}/api/report`;
    const blob = new Blob([JSON.stringify(report)], { type: 'application/json' });

    // sendBeacon is fire-and-forget — works even during page unload
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      // Fallback for older browsers
      fetch(url, { method: 'POST', body: blob, keepalive: true }).catch(() => {});
    }
  }
}

// Singleton instance
export const analytics = new SessionAnalytics();

// Auto-save on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    analytics.save();
    analytics.sendReport();
  });
}
